import { useEffect, useState } from 'react';
import { supabase, handleSupabaseError, retryOperation } from '../lib/supabase';
import type { Plant } from '../types/plant';
import type { Zone } from '../types/zone';
import { isPlantCompatible, zoneToNumber } from '../utils/zoneUtils';

type Difficulty = 'beginner' | 'intermediate' | 'advanced' | null;

// Special IDs for recommended plants
const COLD_ZONE_PLANTS = [
  'b5875bd4-a05d-4b31-bbd8-751fb34efd83', // Spinach
  '375aff03-ebbe-4a1e-a6ea-a426c93d574d', // Nasturtium
  'f8851f71-a3bc-409e-a3ed-3a14474ad0a1'  // Cabbage
];

const WARM_ZONE_PLANTS = [
  '78e1cc7e-efe7-418f-a7ad-ccb1772b9fd8', // Tomatoes
  '95bdbab3-387b-4e5f-8d3f-f58a936b1528', // Cucumber
  'c55f7763-b6f8-41f8-9764-5a5fe3df047e'  // Zinnias
];

export function usePlants(
  zone: Zone | null = null, 
  difficulty: Difficulty = null,
  sunlight: string | null = null,
  season: string | null = null,
  forHomePage: boolean = false,
  limit?: number
) {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchPlants() {
      try {
        setLoading(true);
        setError(null);
        
        let query = supabase.from('plants').select('*');

        if (forHomePage && zone) {
          // For home page recommendations, use specific plant IDs based on zone
          const zoneNum = zoneToNumber(zone);
          const recommendedIds = zoneNum <= 3.5 ? COLD_ZONE_PLANTS : WARM_ZONE_PLANTS;
          query = query.in('id', recommendedIds);
        } else {
          // Regular filtering
          if (difficulty) {
            query = query.eq('difficulty_level', difficulty);
          }
          if (sunlight) {
            query = query.eq('sunlight_requirements', sunlight);
          }
          if (season) {
            query = query.eq('season', season);
          }
        }

        // Add ordering
        query = query.order('name');

        // Add limit if specified
        if (limit) {
          query = query.limit(limit);
        }

        // Use retryOperation with the abort signal
        const { data, error } = await retryOperation(
          () => query,
          3, // max retries
          1000, // initial delay
          abortController.signal
        );
            
        if (error) throw error;

        // Filter plants by zone compatibility if not using forHomePage
        let filteredPlants = data || [];
        if (zone && !forHomePage) {
          filteredPlants = filteredPlants.filter(plant => 
            isPlantCompatible(zone, plant.zone_min, plant.zone_max)
          );
        }

        // Sort plants alphabetically by name
        const sortedPlants = filteredPlants.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        setPlants(sortedPlants);
      } catch (err) {
        if (abortController.signal.aborted) return;
        const errorMessage = handleSupabaseError(err);
        console.error('Error fetching plants:', err);
        setError(errorMessage);
        setPlants([]); // Reset plants on error
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchPlants();

    // Cleanup function to abort any pending requests
    return () => {
      abortController.abort();
    };
  }, [zone, difficulty, sunlight, season, forHomePage, limit]);

  return { plants, loading, error };
}